import { DASHBOARD_DRAG_APPEND_TARGET_ID } from '../dashboardDragReorder.js';
import { getAvailableHomeDashboardSectionIds as getAvailableSectionIdsByUser } from './homeDashboardSectionAccess.js';

export const HOME_DASHBOARD_LAYOUT_VERSION = 1;

/** 宽于 15.3″ MacBook Air 默认视口（1710）后，主区四区块改为 2×2。与 index.css 1720px 断点一致。 */
export const HOME_DASHBOARD_WIDE_LAYOUT_MIN_PX = 1720;

export const HOME_DASHBOARD_SECTION_IDS = {
	STAT_CARDS: 'statCards',
	HOME_CHARTS: 'homeCharts',
	PROCESSING_ORDERS: 'processingOrders',
	OVERDUE: 'overdue',
};

export const HOME_DASHBOARD_SECTION_LABELS = {
	[HOME_DASHBOARD_SECTION_IDS.STAT_CARDS]: '统计卡片',
	[HOME_DASHBOARD_SECTION_IDS.HOME_CHARTS]: '经营看板',
	[HOME_DASHBOARD_SECTION_IDS.PROCESSING_ORDERS]: '处理中订单',
	[HOME_DASHBOARD_SECTION_IDS.OVERDUE]: '超期统计',
};

const ALL_SECTION_IDS = Object.values(HOME_DASHBOARD_SECTION_IDS);

const HOME_CHARTS_SECTION_ROLES = new Set(['manager', 'admin']);

export const getAvailableHomeDashboardSectionIds = (roleOrUserInfo) => {
	if (roleOrUserInfo && typeof roleOrUserInfo === 'object') {
		return getAvailableSectionIdsByUser(roleOrUserInfo);
	}
	const role = roleOrUserInfo;
	if (HOME_CHARTS_SECTION_ROLES.has(role)) return [...ALL_SECTION_IDS];
	return ALL_SECTION_IDS.filter((id) => id !== HOME_DASHBOARD_SECTION_IDS.HOME_CHARTS);
};

export const buildDefaultHomeDashboardLayout = (roleOrUserInfo = 'manager') => {
	const ids = getAvailableHomeDashboardSectionIds(roleOrUserInfo);
	return {
		version: HOME_DASHBOARD_LAYOUT_VERSION,
		sections: ids.map((id, order) => ({ id, order, hidden: false })),
	};
};

const sanitizeSection = (item, allowedIds) => {
	if (!item || typeof item !== 'object') return null;
	const id = typeof item.id === 'string' ? item.id.trim() : '';
	if (!allowedIds.has(id)) return null;
	const order = Number.isFinite(Number(item.order)) ? Math.max(0, Math.round(Number(item.order))) : 0;
	const hidden = Boolean(item.hidden);
	return { id, order, hidden };
};

export const normalizeHomeDashboardLayout = (layout, roleOrUserInfo = 'manager') => {
	const allowedIds = new Set(getAvailableHomeDashboardSectionIds(roleOrUserInfo));
	const defaults = buildDefaultHomeDashboardLayout(roleOrUserInfo);
	const defaultById = new Map(defaults.sections.map((s) => [s.id, s]));

	if (!layout || typeof layout !== 'object') {
		return defaults;
	}

	const parsed = Array.isArray(layout.sections) ? layout.sections.map((item) => sanitizeSection(item, allowedIds)).filter(Boolean) : [];

	const byId = new Map();
	for (const section of parsed) {
		if (!byId.has(section.id)) {
			byId.set(section.id, section);
		}
	}

	for (const id of allowedIds) {
		if (!byId.has(id)) {
			byId.set(id, { ...defaultById.get(id) });
		}
	}

	const sections = [...byId.values()].sort((a, b) => a.order - b.order || ALL_SECTION_IDS.indexOf(a.id) - ALL_SECTION_IDS.indexOf(b.id));
	return {
		version: HOME_DASHBOARD_LAYOUT_VERSION,
		sections: sections.map((section, index) => ({ ...section, order: index })),
	};
};

export const resolveVisibleHomeDashboardSections = (layout, roleOrUserInfo = 'manager') => {
	const normalized = normalizeHomeDashboardLayout(layout, roleOrUserInfo);
	return normalized.sections.filter((section) => !section.hidden);
};

export const reorderHomeDashboardSections = (layout, sourceId, targetId, role = 'manager') => {
	const normalized = normalizeHomeDashboardLayout(layout, role);
	if (!sourceId || !targetId || sourceId === targetId) return normalized;

	if (targetId === DASHBOARD_DRAG_APPEND_TARGET_ID) {
		const fromIndex = normalized.sections.findIndex((s) => s.id === sourceId);
		if (fromIndex < 0 || fromIndex >= normalized.sections.length - 1) return normalized;
		const nextSections = [...normalized.sections];
		const [moved] = nextSections.splice(fromIndex, 1);
		nextSections.push(moved);
		return {
			...normalized,
			sections: nextSections.map((section, index) => ({ ...section, order: index })),
		};
	}

	const fromIndex = normalized.sections.findIndex((s) => s.id === sourceId);
	const toIndex = normalized.sections.findIndex((s) => s.id === targetId);
	if (fromIndex < 0 || toIndex < 0) return normalized;

	// 瀑布流两列：松手落在哪块就与哪块交换位置，避免「插到前面」在紧邻项上空操作。
	const nextSections = [...normalized.sections];
	const sourceSection = nextSections[fromIndex];
	nextSections[fromIndex] = nextSections[toIndex];
	nextSections[toIndex] = sourceSection;
	return {
		...normalized,
		sections: nextSections.map((section, index) => ({ ...section, order: index })),
	};
};

export const toggleHomeDashboardSectionHidden = (layout, sectionId, role = 'manager') => {
	const normalized = normalizeHomeDashboardLayout(layout, role);
	const index = normalized.sections.findIndex((s) => s.id === sectionId);
	if (index < 0) return normalized;

	const nextSections = normalized.sections.map((section, i) => (i === index ? { ...section, hidden: !section.hidden } : section));
	return { ...normalized, sections: nextSections };
};

export const setHomeDashboardSectionHidden = (layout, sectionId, hidden, role = 'manager') => {
	const normalized = normalizeHomeDashboardLayout(layout, role);
	const index = normalized.sections.findIndex((s) => s.id === sectionId);
	if (index < 0) return normalized;

	const nextSections = normalized.sections.map((section, i) => (i === index ? { ...section, hidden: Boolean(hidden) } : section));
	return { ...normalized, sections: nextSections };
};
